import * as Sentry from "@sentry/vue";
import useHelpers from "composables/useHelpers.js";
import { throttle } from "lodash";
import { ref } from "vue";

export const SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM = true;

export function sentryBeforeSend(event, hint) {
  const error = hint?.originalException;
  const errorMessage = String(
    error?.message || event?.exception?.values?.[0]?.value || "",
  );

  // Check if the error is an Axios 401 error
  if (error?.isAxiosError && error.response?.status === 401) {
    // Discard the error by returning null
    return null;
  }

  // Discard browser-extension / third-party injected-script errors.
  // These originate from inline scripts a content script evaluates
  // twice in the same scope (stack frames are <anonymous>), e.g.
  // "Failed to execute 'appendChild' on 'Node': Identifier
  // 'scriptElement' has already been declared". Not our code.
  if (
    errorMessage.includes("Failed to execute 'appendChild' on 'Node'") &&
    errorMessage.includes("has already been declared")
  ) {
    return null;
  }

  // Discard the Sentry feedback widget's own teardown crash.
  // @sentry-internal/feedback@8 calls shadowRoot.removeChild() in
  // removeFromDom() without guarding against an already-detached
  // node, so a double teardown after a successful submit throws
  // "Failed to execute 'removeChild' on 'Node': The node to be
  // removed is not a child of this node." The feedback is already
  // sent by then, so this is teardown noise, not a real failure
  // (fixed upstream in v10 via el.remove()). Match the message AND
  // a feedback-widget stack frame so we don't swallow app errors.
  const stackFrames = event?.exception?.values?.[0]?.stacktrace?.frames ?? [];
  const isFeedbackWidgetFrame = stackFrames.some((frame) => {
    return (
      frame?.filename?.includes("@sentry-internal/feedback") ||
      frame?.function?.includes("removeFromDom")
    );
  });

  if (
    errorMessage.includes("Failed to execute 'removeChild' on 'Node'") &&
    errorMessage.includes("not a child of this node") &&
    isFeedbackWidgetFrame === true
  ) {
    return null;
  }

  // Group network errors into a separate low-priority issue
  if (
    (error?.isAxiosError && error?.message?.includes?.("Network Error")) ||
    (error?.name === "AxiosError" && errorMessage.includes("Network Error")) ||
    errorMessage.includes("AxiosError Network Error") ||
    errorMessage.includes("timeout exceeded") ||
    errorMessage.includes("timeout of") ||
    errorMessage.includes("ERR_NETWORK") ||
    errorMessage.includes("ERR_INTERNET_DISCONNECTED") ||
    errorMessage.includes("Failed to fetch") ||
    (error?.name === "TypeError" && errorMessage.includes("Failed to fetch"))
  ) {
    // Normalize all network errors to a single fingerprint
    event.fingerprint = ["network-error"];
    event.level = "info";
    event.tags = {
      ...event.tags,
      "error-type": "network-related",
      "auto-handled": "true",
    };
    // Override the error message to group them together
    if (event.exception?.values?.[0]) {
      event.exception.values[0].value =
        "Network connectivity issue (token refresh or connection failure)";
      event.exception.values[0].type = "NetworkConnectivityError";
    }
    return event;
  }

  // Group module loading errors into a single low-priority issue
  if (
    errorMessage.includes("Failed to load module script") ||
    errorMessage.includes("Failed to fetch dynamically imported module") ||
    errorMessage.includes("error loading dynamically imported module") ||
    errorMessage.includes("Importing a module script failed") ||
    errorMessage.match(/Loading chunk \d+ failed/) ||
    errorMessage.includes("ChunkLoadError") ||
    errorMessage.includes("Load failed") ||
    errorMessage.includes("Unable to preload CSS") ||
    errorMessage.includes("is not a valid JavaScript MIME type") ||
    (error?.name === "TypeError" &&
      errorMessage.includes("onunhandledrejection"))
  ) {
    // Normalize all module loading errors to a single fingerprint
    event.fingerprint = ["module-loading-error"];
    event.level = "info";
    event.tags = {
      ...event.tags,
      "error-type": "deployment-related",
      "auto-handled": "true",
    };
    // Override the error message to group them together
    if (event.exception?.values?.[0]) {
      event.exception.values[0].value =
        "Module loading failed during deployment (auto-handled)";
      event.exception.values[0].type = "DeploymentModuleError";
    }
    return event;
  }

  return event;
}

export default {
  install: async (app) => {
    if (
      import.meta.env.VITE_APP_ENV !== "development" &&
      import.meta.env.VITE_APP_ENV !== "staging"
    ) {
      const { version } = await import("~/package.json");

      const {
        schoolId,
        employeeId,
        studentId,
        user,
        firstName,
        schoolName,
        currentShortage,
      } = useHelpers();

      const hasUser = !!user;

      Sentry.setUser({
        fullName: firstName ?? "",
        email: user?.email ?? "",
      });

      const tags = ref({
        "catwise-version": version,
        "catwise-school-name": schoolName,
        "catwise-school-id": schoolId,
        "catwise-employee-id": employeeId,
        "catwise-current-shortage": currentShortage,
        "catwise-student-id": studentId,
      });

      const translations = {
        triggerLabel: "Probleem melden",
        triggerAriaLabel: "Probleem melden",
        formTitle: "Probleem melden",
        submitButtonLabel: "Versturen",
        cancelButtonLabel: "Annuleren",
        confirmButtonLabel: "Bevestigen",
        addScreenshotButtonLabel: "Voeg een screenshot toe",
        removeScreenshotButtonLabel: "Verwijder screenshot",
        nameLabel: "Naam",
        namePlaceholder: "Vul je naam in",
        emailPlaceholder: "Vul je e-mailadres in",
        isRequiredLabel: "Verplicht",
        messageLabel: "Bericht",
        messagePlaceholder:
          "Let op: deze feedback gaat naar Catwise, niet naar je school.\n\nWat wil je ons vertellen? Voeg een screenshot toe indien nodig!",
        successMessageText: "Bedankt voor je feedback!",
      };

      const sentryFeedbackState = {
        button: null,
        labelSpans: [],
        labelSpansOriginalDisplay: [],
        bodyClassObserver: null,
      };

      const captureSentryFeedbackTextNodes = (button) => {
        if (!button || sentryFeedbackState.labelSpans.length > 0) {
          return;
        }

        const spans = Array.from(button.querySelectorAll("span"));
        const labelSpans = spans.filter((s) => {
          const txt = s.textContent?.trim();
          return typeof txt === "string" && txt.length > 0;
        });

        sentryFeedbackState.button = button;
        sentryFeedbackState.labelSpans = labelSpans;
        sentryFeedbackState.labelSpansOriginalDisplay = labelSpans.map(
          (spanNode) => spanNode.style.display || "",
        );

        ensureBodyClassObserver();
        syncSentryFeedbackFromBodyClass();
      };

      const hideSentryFeedbackText = () => {
        if (sentryFeedbackState.labelSpans.length === 0) {
          return;
        }

        sentryFeedbackState.labelSpans.forEach((span) => {
          span.style.display = "none";
        });
      };

      const showSentryFeedbackText = () => {
        if (sentryFeedbackState.labelSpans.length === 0) {
          return;
        }

        sentryFeedbackState.labelSpans.forEach((span, index) => {
          const original = sentryFeedbackState.labelSpansOriginalDisplay[index];
          span.style.display = original || "";
        });
      };

      const syncSentryFeedbackFromBodyClass = () => {
        if (document.body.classList.contains("overflow-hidden")) {
          return hideSentryFeedbackText();
        }

        showSentryFeedbackText();
      };

      const ensureBodyClassObserver = () => {
        if (sentryFeedbackState.bodyClassObserver || !document.body) {
          return;
        }

        const observer = new MutationObserver((mutations) => {
          const hasClassChange = mutations.some((mutation) => {
            return (
              mutation.type === "attributes" &&
              mutation.attributeName === "class"
            );
          });

          if (!hasClassChange) {
            return;
          }

          syncSentryFeedbackFromBodyClass();
        });

        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ["class"],
        });

        sentryFeedbackState.bodyClassObserver = observer;

        syncSentryFeedbackFromBodyClass();
      };

      Sentry.init({
        app,
        dsn: "https://248bce06f2d99e24acf38a2ce1125cc9@o4506592533610496.ingest.sentry.io/4506672063905792",
        environment: import.meta.env.VITE_APP_ENV,
        trackComponents: false, // trackComponents disabled to reduce span volume
        initialScope: () => ({
          tags: tags.value,
        }),
        integrations: [
          Sentry.browserTracingIntegration({
            instrumentNavigation: false,
            instrumentPageLoad: false,
          }),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
          Sentry.feedbackIntegration({
            ...translations,
            autoInject: !SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM,
            colorScheme: "light",
            enableScreenshot: true,
            showBranding: false,
            showName: !hasUser,
            showEmail: !hasUser,
            tags: tags.value,
            useSentryUser: {
              name: "fullName",
              email: "email",
            },
          }),
        ],
        tracesSampleRate: 0.01, // 1% of transactions sampled (reduced from 10% to cut span volume)
        replaysSessionSampleRate: 0.001, // 0.1% of all sessions randomly recorded
        replaysOnErrorSampleRate: 0.5, // 50% of error sessions (reduced from 100% to save quota)
        beforeSend: sentryBeforeSend,
      });

      const updateSentryFeedbackPadding = (button) => {
        if (!button) {
          return;
        }

        const applyPadding = () => {
          const windowWidth = window.innerWidth;

          if (windowWidth < 1024) {
            button.style.padding = "10px";
          } else {
            button.style.padding = "14px";
          }
        };

        applyPadding();

        window.addEventListener("resize", throttle(applyPadding, 250));
      };

      const setupSentryFeedbackStyling = () => {
        const tryApply = () => {
          const hostElement = document.querySelector("#sentry-feedback");

          if (!hostElement) {
            return false;
          }

          const root = hostElement.shadowRoot || hostElement;
          const button = root.querySelector("button");

          if (!button) {
            return false;
          }

          updateSentryFeedbackPadding(button);
          captureSentryFeedbackTextNodes(button);

          return true;
        };

        if (tryApply()) {
          return;
        }

        const observer = new MutationObserver(() => {
          if (tryApply()) {
            observer.disconnect();
          }
        });

        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      };

      // The styling and label handling target the floating actor button, which
      // isn't injected when the feedback button lives in the menu.
      if (SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM === false) {
        setupSentryFeedbackStyling();

        ensureBodyClassObserver();
      }

      // Report if Font Awesome fallback was triggered
      if (window.__fontAwesomeFallbackLoaded) {
        Sentry.captureMessage(
          "Font Awesome webfont failed to load, fallback kit loaded",
          "warning",
        );
      }

      app.config.globalProperties.$sentryFeedback = {
        hideText: hideSentryFeedbackText,
        showText: showSentryFeedbackText,
      };
    }
  },
};
